<template>
    <div ref="scroll" class="smt-scroll">
        <table v-if="groups.length" class="smt-table">
            <tbody v-for="group in groups" :key="group.date" class="smt-group">
                <tr class="smt-day">
                    <th :colspan="columns.length" scope="colgroup">
                        <span class="smt-day__label">{{ group.label }}</span>
                        <span class="smt-day__count">{{ countLabel(group.count) }}</span>
                    </th>
                </tr>
                <tr class="smt-head">
                    <th
                        v-for="column in columns"
                        :key="column.key"
                        scope="col"
                        :class="[`smt-col-${column.key}`, { 'is-sortable': column.sortable, 'is-sorted': sortBy === column.key }]"
                        :aria-sort="
                            column.sortable && sortBy === column.key ? (order === 'desc' ? 'descending' : 'ascending') : null
                        "
                        @click="column.sortable ? $emit('sort', column.key) : null"
                    >
                        <span class="smt-head__inner">
                            {{ column.label }}
                            <ChevronDown
                                v-if="column.sortable"
                                :size="14"
                                class="smt-head__chevron"
                                :class="{ 'is-desc': sortBy === column.key && order === 'desc' }"
                            />
                        </span>
                    </th>
                </tr>
                <tr v-for="row in group.rows" :key="row.uuid" class="smt-row">
                    <td class="smt-col-time poppinsRegular">{{ row.time || timeOf(row) }}</td>
                    <td class="smt-col-type">
                        <span class="smt-badge poppinsMedium" :style="badgeStyle(row.type)">{{ typeLabel(row) }}</span>
                    </td>
                    <td class="smt-col-product poppinsRegular">{{ row.item ? row.item.name : '' }}</td>
                    <td class="smt-col-variation" :class="variationClass(row)" :title="variationTooltip(row)">
                        {{ variationOf(row) }}
                    </td>
                    <td class="smt-col-staff poppinsRegular">{{ row.user && row.user.name ? row.user.name : '—' }}</td>
                    <td class="smt-col-note">
                        <MovementNoteCell
                            :value="row.note"
                            :editable="canEditNotes"
                            @save="$emit('save-note', { uuid: row.uuid, note: $event })"
                        />
                    </td>
                </tr>
            </tbody>
        </table>

        <div v-else-if="!loading" class="smt-empty poppinsRegular">
            {{ $t('stock.movements.empty') }}
        </div>

        <div ref="sentinel" class="smt-sentinel" aria-hidden="true"></div>

        <div v-if="loading" class="smt-loading">
            <div
                class="ppv2-animate-spin ppv2-rounded-full ppv2-h-6 ppv2-w-6 ppv2-border-2 ppv2-border-purple-600 ppv2-border-t-transparent"
            ></div>
        </div>
    </div>
</template>

<script>
import { ChevronDown } from 'lucide-vue'
import MovementNoteCell from './MovementNoteCell.vue'
import { movementTypeMeta, variationLabel, variationTitle } from './stockUtils'

export default {
    name: 'StockMovementsTable',
    components: { ChevronDown, MovementNoteCell },
    props: {
        /** `[{ date, label, count, rows: Movement[] }]` */
        groups: {
            type: Array,
            default: () => [],
        },
        sortBy: {
            type: String,
            default: 'time',
        },
        order: {
            type: String,
            default: 'asc',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        hasMore: {
            type: Boolean,
            default: false,
        },
        /** Permission serveur (« Modifier un produit ») ; faux ⇒ notes en lecture seule. */
        canEditNotes: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        columns() {
            return [
                { key: 'time', label: this.$t('stock.movements.columns.time'), sortable: true },
                { key: 'type', label: this.$t('stock.movements.columns.type'), sortable: true },
                { key: 'product', label: this.$t('stock.movements.columns.product'), sortable: true },
                { key: 'variation', label: this.$t('stock.movements.columns.variation'), sortable: true },
                { key: 'staff', label: this.$t('stock.movements.columns.staff'), sortable: true },
                { key: 'note', label: this.$t('stock.movements.columns.note'), sortable: false },
            ]
        },
    },
    methods: {
        countLabel(count) {
            const n = Number(count) || 0
            return this.$t(n > 1 ? 'stock.movements.count_other' : 'stock.movements.count_one', { count: n })
        },
        typeLabel(row) {
            const meta = movementTypeMeta(row.type)
            const translated = this.$te(meta.labelKey) ? this.$t(meta.labelKey) : null
            return translated || row.type_label || row.type
        },
        badgeStyle(type) {
            const meta = movementTypeMeta(type)
            return { backgroundColor: meta.bg, color: meta.color }
        },
        timeOf(row) {
            if (!row.occurred_at) return ''
            // `occurred_at` est déjà exprimé dans le fuseau de l'établissement : on lit l'heure littérale
            const match = String(row.occurred_at).match(/T(\d{2}:\d{2})/)
            return match ? match[1] : ''
        },
        variationOf(row) {
            return row.variation_label || variationLabel(row.stock_before, row.stock_after)
        },
        variationTooltip(row) {
            return variationTitle(row.stock_before, row.stock_after)
        },
        variationClass(row) {
            if (row.stock_before === null || row.stock_after === null) return 'is-neutral'
            const delta = Number(row.quantity_delta)
            if (delta > 0) return 'is-up'
            if (delta < 0) return 'is-down'
            return 'is-zero'
        },
        observeSentinel() {
            if (typeof IntersectionObserver === 'undefined' || !this.$refs.sentinel) return
            this._observer = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0]
                    if (entry && entry.isIntersecting && this.hasMore && !this.loading) {
                        this.$emit('load-more')
                    }
                },
                { root: this.$refs.scroll, rootMargin: '120px 0px' }
            )
            this._observer.observe(this.$refs.sentinel)
        },
        /** Ré-arme l'observateur : s'il n'y a pas assez de lignes pour remplir la zone, la sentinelle
         *  est déjà visible et aucun nouvel événement d'intersection n'arriverait. */
        reobserveSentinel() {
            if (!this._observer || !this.$refs.sentinel) return
            this._observer.unobserve(this.$refs.sentinel)
            this._observer.observe(this.$refs.sentinel)
        },
    },
    watch: {
        loading(isLoading) {
            if (!isLoading && this.hasMore) this.$nextTick(this.reobserveSentinel)
        },
    },
    mounted() {
        this.observeSentinel()
    },
    beforeDestroy() {
        if (this._observer) this._observer.disconnect()
    },
}
</script>

<style scoped lang="scss">
// Valeurs reprises de la maquette PO « Mouvements de stock.html »
.smt-scroll {
    position: relative;
    overflow: auto;
    max-height: calc(100vh - 340px);
    min-height: 200px;
    border-radius: 12px;
    scrollbar-width: thin;
    scrollbar-color: #d5bdf0 transparent;

    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }

    &::-webkit-scrollbar-track,
    &::-webkit-scrollbar-corner {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #d5bdf0;
        border-radius: 999px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #b06ae4;
    }
}

.smt-table {
    width: 100%;
    min-width: 820px;
    border-collapse: separate;
    border-spacing: 0;
}

.smt-day th {
    position: sticky;
    top: 0;
    z-index: 4;
    height: 44px;
    padding: 0 26px;
    background: #ecdcfa;
    border-bottom: 1px solid #ddc4f3;
    text-align: left;
}

.smt-day__label {
    font-size: 13px;
    font-weight: 600;
    color: #000;
}

.smt-day__count {
    margin-left: 10px;
    font-size: 11px;
    font-weight: 400;
    color: #6b7480;
}

.smt-head th {
    background: #f3eafa;
    color: #021832;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    text-align: left;
    padding: 10px 12px;
    white-space: nowrap;
    user-select: none;

    &.is-sortable {
        cursor: pointer;
    }

    &.is-sortable:hover,
    &.is-sorted {
        color: #7d2dbd;
    }

    &.smt-col-time {
        padding-left: 26px;
    }

    &.smt-col-variation {
        text-align: right;
    }

    // td 12px + bouton de note 10px : l'en-tête s'aligne sur le texte de la note
    &.smt-col-note {
        padding: 10px 26px 10px 22px;
    }
}

.smt-head__inner {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.smt-head__chevron {
    opacity: 0.45;
    transition: transform 0.15s ease;
}

.is-sorted .smt-head__chevron {
    opacity: 1;
}

.smt-head__chevron.is-desc {
    transform: rotate(180deg);
}

.smt-row {
    background: #fff;
}

.smt-row td {
    padding: 14px 12px;
    border-bottom: 1px solid #f4eff9;
    font-size: 13px;
    color: #021832;
    vertical-align: middle;
}

.smt-row:hover td {
    background: #fbf9fe;
}

.smt-row td.smt-col-time {
    padding-left: 26px;
}

.smt-row td.smt-col-note {
    padding: 9px 26px 9px 12px;
}

.smt-col-time {
    width: 76px;
    font-variant-numeric: tabular-nums;
    color: #6b7480;
}

.smt-col-type {
    width: 120px;
}

.smt-col-product {
    font-weight: 500;
}

.smt-col-variation {
    width: 96px;
    text-align: right;
}

td.smt-col-variation {
    font-size: 14px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;

    &.is-up {
        color: #15803d;
    }

    &.is-down {
        color: #b91c1c;
    }

    &.is-neutral {
        color: #7d2dbd;
    }

    &.is-zero {
        color: #9aa4b2;
    }
}

.smt-col-staff {
    width: 190px;
    white-space: nowrap;
}

td.smt-col-staff {
    color: #2a313a;
}

.smt-col-note {
    min-width: 240px;
}

.smt-badge {
    display: inline-block;
    min-width: 64px;
    box-sizing: border-box;
    text-align: center;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.02em;
    white-space: nowrap;
}

.smt-empty {
    padding: 48px 16px;
    text-align: center;
    color: #6b7280;
    font-size: 14px;
}

.smt-sentinel {
    height: 1px;
}

.smt-loading {
    display: flex;
    justify-content: center;
    padding: 16px;
}

.dark .smt-row {
    background: transparent;
}

.dark .smt-row td {
    color: #fff;
    border-bottom-color: rgba(255, 255, 255, 0.08);
}

.dark .smt-row:hover td {
    background: rgba(255, 255, 255, 0.04);
}

.dark td.smt-col-time,
.dark td.smt-col-staff {
    color: #bfbfbf;
}
</style>
